/**
 * Streaming-safe Markdown renderer.
 *
 * Wraps ``react-markdown`` with:
 *   - GFM (tables, task lists, strikethrough).
 *   - A pre-processor that terminates unclosed code fences and inline
 *     code, so a half-streamed Claude chunk renders cleanly without
 *     "eating" subsequent paragraphs.
 *   - A ``CodeBlock`` substitution for fenced code (syntax highlighting
 *     + copy button, reusing the existing docs component).
 *   - Safe external link handling (``target="_blank"`` / ``rel`` set).
 *
 * The goal is a renderer you can feed every source chunk as it
 * arrives from Claude. It renders the latest received text directly;
 * smoothing belongs upstream in the transport, not in this markdown
 * component.
 */

import * as React from 'react';
import type { Components } from 'react-markdown';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CodeBlock, InlineCode } from '@/components/docs/CodeBlock';
import { codePanePathCandidates, openCodePaneFile } from '@/lib/notebook/codePaneSync';
import { cn } from '@/lib/utils/cn';

export interface StreamingMarkdownProps {
  children: string;
  /**
   * When true (the default), unterminated code fences are closed with a
   * synthetic fence so the rest of the document doesn't render as code.
   * Set false for already-finalized content where you'd rather see the
   * raw text.
   */
  closeUnterminated?: boolean;
  /**
   * Project cwd used to resolve inline file references in agent messages.
   * Omit outside the Agent Pane so markdown remains plain documentation.
   */
  codePaneCwd?: string | null;
  /**
   * True while the source is still streaming in (typewriter reveal). Fenced
   * code is rendered as plain ``<pre>`` in this state instead of running the
   * Prism highlighter, which would otherwise re-tokenize the entire (growing)
   * code block on every reveal frame -- O(n^2) over the stream. Highlighting
   * kicks in once the block finalizes (``streaming`` flips false).
   */
  streaming?: boolean;
  className?: string;
}

interface MarkdownCodePaneContextValue {
  cwd: string | null;
  source: string;
  streaming: boolean;
}

interface CodePaneLinkTarget {
  path: string;
  candidates: string[];
}

/**
 * Terminate unclosed Markdown containers so partial input renders
 * sensibly. Today only code fences get this treatment — they are the
 * only container that will absorb the rest of the document if left
 * open.
 */
function closeUnterminatedFences(input: string): string {
  // Count lines that are ``` or ~~~ at start-of-line (possibly after
  // whitespace). Odd count → one is unclosed.
  const fenceRe = /^[\s]*(?:```|~~~)/gm;
  const matches = input.match(fenceRe);
  if (!matches) return input;
  if (matches.length % 2 === 0) return input;
  return `${input}\n\`\`\``;
}

function _hasIncompleteInlineCode(input: string): boolean {
  // An odd count of unescaped backticks means the final segment is
  // still inside a code span. This is cheap and good enough.
  const ticks = input.match(/(?<!\\)`+/g) ?? [];
  const total = ticks.reduce((sum, run) => sum + run.length, 0);
  return total % 2 !== 0;
}

// Module-level stable references. Defining components or plugin
// arrays inline in JSX would give every override a fresh function
// identity per render; react-markdown hands those new identities to
// React.createElement, and React's reconciler then unmounts and
// remounts the entire rendered subtree on every chunk. Hoisting them
// here keeps reconciliation cheap and visually stable while the
// typewriter buffer drips text in.
const REMARK_PLUGINS = [remarkGfm];
const MarkdownCodePaneContext = React.createContext<MarkdownCodePaneContextValue>({
  cwd: null,
  source: 'markdown',
  streaming: false,
});

function getTableCellAlignClass(align: string | undefined): string {
  switch (align) {
    case 'center':
      return 'text-center';
    case 'right':
      return 'text-right';
    default:
      return 'text-left';
  }
}

const MARKDOWN_COMPONENTS: Components = {
  code: ({ className: c, children: ch, ...rest }) => {
    const text = String(ch ?? '');
    const { source, streaming } = React.useContext(MarkdownCodePaneContext);
    const target = useMarkdownCodePaneTarget(text, false);
    const match = /language-([\w-]+)/.exec(c ?? '');
    const isBlock = /\n/.test(text) || match;
    if (isBlock) {
      return (
        <CodeBlock language={match?.[1]} density="compact" className="my-2" streaming={streaming}>
          {text.replace(/\n$/, '')}
        </CodeBlock>
      );
    }
    if (target) {
      return (
        <button
          type="button"
          className="inline cursor-pointer rounded-sm border-0 bg-transparent p-0 align-baseline outline-none focus-visible:ring-1 focus-visible:ring-gray-300 dark:focus-visible:ring-white/20 [&>code]:transition-colors [&>code]:duration-150 hover:[&>code]:border-gray-300 hover:[&>code]:bg-gray-200 dark:hover:[&>code]:border-white/20 dark:hover:[&>code]:bg-[#14304c]"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            openCodePaneFile(target.path, source, target.candidates);
          }}
        >
          <InlineCode className={c} {...(rest as Record<string, unknown>)}>
            {ch}
          </InlineCode>
        </button>
      );
    }
    return (
      <InlineCode className={c} {...(rest as Record<string, unknown>)}>
        {ch}
      </InlineCode>
    );
  },
  pre: ({ children: ch }) => <>{ch}</>,
  a: ({ href, children: ch, ...rest }) => {
    const { source } = React.useContext(MarkdownCodePaneContext);
    const target = useMarkdownCodePaneTarget(href ?? '', true);
    if (target) {
      return (
        <a
          href={href}
          className="cursor-pointer font-medium text-gray-800 underline decoration-gray-300 underline-offset-2 transition-colors hover:text-gray-950 hover:decoration-gray-500 dark:text-gray-100 dark:decoration-gray-600 dark:hover:text-white dark:hover:decoration-gray-400"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            openCodePaneFile(target.path, source, target.candidates);
          }}
          {...(rest as Record<string, unknown>)}
        >
          {ch}
        </a>
      );
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-gray-800 underline decoration-gray-300 underline-offset-2 transition-colors hover:text-gray-950 hover:decoration-gray-500 dark:text-gray-100 dark:decoration-gray-600 dark:hover:text-white dark:hover:decoration-gray-400"
        {...(rest as Record<string, unknown>)}
      >
        {ch}
      </a>
    );
  },
  strong: ({ children: ch, ...rest }) => (
    <strong className="font-medium text-gray-800 dark:text-gray-200" {...(rest as Record<string, unknown>)}>
      {ch}
    </strong>
  ),
  ul: ({ children: ch, ...rest }) => (
    <ul className="my-1.5 list-disc space-y-0.5 pl-5" {...(rest as Record<string, unknown>)}>
      {ch}
    </ul>
  ),
  // Ordered lists use the ``.agent-md-ol`` setup in ``index.css`` —
  // a flexbox layout with a fixed-width marker column. We render the
  // marker as a real ``<span>`` (not a browser-generated list marker
  // or a ``::before`` pseudo) so the user can text-select the digits.
  // Each ``<li>`` receives its computed marker via a ``data-marker``
  // attribute the ``li`` override picks up; the index is taken from
  // the source ``<ol start="N">`` attribute, defaulting to 1.
  ol: ({ children: ch, start, ...rest }) => {
    const startNum = typeof start === 'number' ? start : 1;
    let liIndex = 0;
    const wrapped = React.Children.map(ch, (child) => {
      if (!React.isValidElement(child)) return child;
      const marker = `${startNum + liIndex}.`;
      liIndex++;
      return React.cloneElement(child as React.ReactElement<{ 'data-marker'?: string }>, { 'data-marker': marker });
    });
    return (
      <ol className="agent-md-ol my-1.5 space-y-0.5" {...(rest as Record<string, unknown>)}>
        {wrapped}
      </ol>
    );
  },
  li: ({ children: ch, ...rest }) => {
    const marker = (rest as Record<string, unknown>)['data-marker'] as string | undefined;
    // Ordered-list items wrap their body in ``.agent-md-li-body`` so
    // the grid layout (set up in ``index.css``) only sees two
    // children — the marker span and the body — and won't break
    // inline content (text runs, ``<code>`` spans) into separate
    // grid rows. The body class is harmless on unordered lists; the
    // grid CSS only targets ``.agent-md-ol > li`` so ``<ul>`` items
    // stay block-flow as before.
    return (
      <li className="my-0.5" {...(rest as Record<string, unknown>)}>
        {typeof marker === 'string' && <span className="agent-md-marker">{marker}</span>}
        <div className="agent-md-li-body">{ch}</div>
      </li>
    );
  },
  // Heading scale gives each level a clearly different size so a
  // multi-section markdown doc (the plan panel is the primary
  // consumer) reads as a hierarchy instead of mostly-flat prose.
  // The raw HTML element is downshifted by one — agent ``# title``
  // outputs as a styled ``<h2>`` so the chat surface never owns the
  // page-level ``<h1>`` slot.
  h1: ({ children: ch }) => (
    <h2 className="mt-5 mb-2 font-medium text-[19px] text-gray-900 leading-tight tracking-[-0.015em] dark:text-gray-100">
      {ch}
    </h2>
  ),
  h2: ({ children: ch }) => (
    <h3 className="mt-4 mb-1.5 font-medium text-[16px] text-gray-800 leading-snug dark:text-gray-200">{ch}</h3>
  ),
  h3: ({ children: ch }) => (
    <h4 className="mt-3 mb-1 font-medium text-[14.5px] text-gray-800 leading-snug dark:text-gray-200">{ch}</h4>
  ),
  h4: ({ children: ch }) => (
    <h5 className="mt-2.5 mb-1 font-medium text-[13px] text-gray-600 uppercase tracking-wide dark:text-gray-300">
      {ch}
    </h5>
  ),
  h5: ({ children: ch }) => (
    <h6 className="mt-2 mb-1 font-medium text-[12.5px] text-gray-500 uppercase tracking-wide dark:text-gray-400">
      {ch}
    </h6>
  ),
  h6: ({ children: ch }) => (
    <h6 className="mt-2 mb-1 font-medium text-[12px] text-gray-500 uppercase tracking-wide dark:text-gray-400">{ch}</h6>
  ),
  p: ({ children: ch }) => <p className="my-1 leading-[1.6]">{ch}</p>,
  blockquote: ({ children: ch }) => (
    <blockquote className="my-2 border-gray-200 border-l-2 pl-3 text-gray-600 italic dark:border-white/15 dark:text-gray-400">
      {ch}
    </blockquote>
  ),
  table: ({ children: ch }) => (
    <div className="my-3 overflow-x-auto rounded-md border border-gray-200/80 dark:border-white/10">
      <table className="min-w-full border-separate border-spacing-0 text-[12.5px] leading-relaxed">{ch}</table>
    </div>
  ),
  th: ({ align, children: ch, ...rest }) => (
    <th
      className={cn(
        'border-gray-200 border-b bg-gray-50/80 px-2.5 py-1.5 align-top font-semibold text-gray-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-200',
        getTableCellAlignClass(align)
      )}
      {...(rest as Record<string, unknown>)}
    >
      {ch}
    </th>
  ),
  td: ({ align, children: ch, ...rest }) => (
    <td
      className={cn(
        'border-gray-200 border-b px-2.5 py-1.5 align-top text-gray-700 dark:border-white/10 dark:text-gray-300',
        getTableCellAlignClass(align)
      )}
      {...(rest as Record<string, unknown>)}
    >
      {ch}
    </td>
  ),
  input: ({ checked, type, ...rest }) => {
    if (type !== 'checkbox') {
      return <input checked={checked} type={type} {...(rest as Record<string, unknown>)} />;
    }
    return (
      <input
        checked={checked}
        type="checkbox"
        readOnly
        className="mr-2 size-3.5 translate-y-0.5 rounded border-gray-300 text-sky-500 dark:border-white/20 dark:bg-white/5"
        {...(rest as Record<string, unknown>)}
      />
    );
  },
  hr: () => <hr className="my-4 border-gray-200 dark:border-white/10" />,
};

export const StreamingMarkdown = React.memo(function StreamingMarkdown({
  children,
  closeUnterminated = true,
  codePaneCwd = null,
  streaming = false,
  className,
}: StreamingMarkdownProps) {
  const safe = React.useMemo(() => {
    let text = closeUnterminated ? closeUnterminatedFences(children) : children;
    if (closeUnterminated && _hasIncompleteInlineCode(text)) {
      text = `${text}​`; // ZWSP nudges react-markdown past the half-open state
    }
    return text;
  }, [children, closeUnterminated]);

  // Stable context value so the code/link overrides don't see a fresh
  // identity (and re-run their context reads) on every typewriter frame.
  const codePaneValue = React.useMemo<MarkdownCodePaneContextValue>(
    () => ({ cwd: codePaneCwd, source: 'agent', streaming }),
    [codePaneCwd, streaming]
  );

  return (
    <MarkdownCodePaneContext.Provider value={codePaneValue}>
      <div
        className={cn(
          'prose prose-sm dark:prose-invert max-w-none break-words text-gray-700 dark:text-gray-300',
          className
        )}
      >
        <Markdown remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
          {safe}
        </Markdown>
      </div>
    </MarkdownCodePaneContext.Provider>
  );
});

function useMarkdownCodePaneTarget(value: string, allowLinkHref: boolean): CodePaneLinkTarget | null {
  const { cwd } = React.useContext(MarkdownCodePaneContext);
  if (!cwd) return null;
  const reference = normalizeFileReference(value, allowLinkHref);
  if (!reference) return null;
  const candidates = codePanePathCandidates(reference, cwd);
  const path = candidates[0];
  return path ? { path, candidates } : null;
}

function normalizeFileReference(value: string, allowLinkHref: boolean): string | null {
  const trimmed = value.trim().replace(/^@/, '');
  if (!trimmed) return null;
  if (allowLinkHref && /^(?:[a-z][a-z0-9+.-]*:|#)/i.test(trimmed)) return null;
  if (!allowLinkHref && !looksLikeInlineFileReference(trimmed)) return null;
  return trimmed.replace(/:(\d+)(?::\d+)?$/, '');
}

function looksLikeInlineFileReference(value: string): boolean {
  if (/\s/.test(value)) return false;
  if (value.includes('/') || value.startsWith('./') || value.startsWith('../') || value.startsWith('~/')) return true;
  return /\.(?:css|html|ipynb|js|json|jsx|md|mdx|py|pyi|rs|sh|toml|ts|tsx|txt|yaml|yml)$/i.test(value);
}
